import { useState } from 'react';
import { IncidentCard } from './components/IncidentCard';
import { DetailedIncidentCard } from './components/DetailedIncidentCard';
import { AlertTriangleIcon, PlusIcon } from 'lucide-react';
import AcknowledgeIncidentModal from '@components/organisms/AcknowledgeIncidentModal';
import { ReportIncidentModal } from '@components/organisms/ReportIncidentModal';
import { Incident, IncidentStatus } from '@models/incident.model';
import { usePendingIncidents } from '@hooks/api/incident/usePendingIncidents';
import { useIncidentsByStatus } from '@hooks/api/incident/useIncidentsByStatus';
import { useAcknowledgeIncident } from '@hooks/api/incident/useAcknowledgeIncident';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { roleRank, ROLE_RANK } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

export function IncidentsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );
  const t = useTranslation('incidents');
  // Sprint 28 change request (global sweep, defect fix). `getIncidentTypeDisplay`/`formatDelay`
  // below used to render raw enum text — reuses this same `incidents` section's `type*`/`unit*`
  // keys (already wired for `t?.reportIncident` above) instead of forking a second map, plus
  // `activityCards.incident.status*` for the PENDING/ACKNOWLEDGED badges.
  const tActivityCards = useTranslation('activityCards');
  const { role } = useAppSelector((state: RootState) => state.user);
  const canReportIncident = roleRank(role) >= ROLE_RANK.SUPERINTENDENT;

  // Fetch pending incidents
  const { data: pendingIncidents = [], isLoading: pendingLoading } =
    usePendingIncidents();

  // Fetch acknowledged incidents
  const { data: acknowledgedIncidents = [], isLoading: acknowledgedLoading } =
    useIncidentsByStatus(IncidentStatus.ACKNOWLEDGED);

  // Acknowledge incident mutation
  const { mutateAsync: acknowledgeIncident, isPending: acknowledgeLoading } =
    useAcknowledgeIncident();

  const openIncidentModal = (incident: Incident) => {
    setSelectedIncident(incident);
    setIsModalOpen(true);
  };

  const handleAcknowledgeConfirm = async () => {
    if (selectedIncident?.incidentId) {
      try {
        await acknowledgeIncident(selectedIncident.incidentId);
        setIsModalOpen(false);
        setSelectedIncident(null);
      } catch (error) {
        console.error('Failed to acknowledge incident:', error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIncident(null);
  };

  // Sprint 28 change request (global sweep, defect fix). `t`/`tActivityCards` can be `undefined`
  // in this file's own tests (`incidents.test.tsx` mocks `useTranslation` to always return
  // `undefined`) — every access below is optional-chained, matching this file's existing
  // `t?.reportIncident || 'Report Incident'` convention above.
  const incidentTypeTextMap: Record<string, string | undefined> = {
    WEATHER: t?.typeWeather,
    ACCIDENT: t?.typeAccident,
    MATERIALS_ISSUE: t?.typeMaterialsIssue,
    EQUIPMENT_ISSUE: t?.typeEquipmentIssue,
    OTHER: t?.typeOther,
  };
  const delayUnitTextMap: Record<string, string | undefined> = {
    HOURS: t?.unitHours,
    DAYS: t?.unitDays,
    WEEKS: t?.unitWeeks,
  };
  const incidentStatusTextMap: Record<string, string | undefined> = {
    PENDING: tActivityCards?.incident?.statusPending,
    ACKNOWLEDGED: tActivityCards?.incident?.statusAcknowledged,
  };

  // Helper function to format incident type
  const getIncidentTypeDisplay = (
    type?: string,
    otherType?: string,
  ): string => {
    if (type === 'OTHER' && otherType) {
      return otherType;
    }
    if (!type) return tActivityCards?.incident?.unknownTypeFallback || 'Unknown';
    return (
      incidentTypeTextMap[type] ||
      tActivityCards?.incident?.unknownTypeFallback ||
      'Unknown'
    );
  };

  // Helper function to format date
  const formatDate = (dateInput: Date | string): string => {
    const date = new Date(dateInput);
    const today = new Date();

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }

    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Helper function to format delay. `'N/A'` is never actually shown — both `IncidentCard` and
  // `DetailedIncidentCard` gate the delay row on `delay !== 'N/A'`, so this is a sentinel, not
  // user-visible text (left untranslated on purpose). The unit suffix below is real, translated
  // text: reuses `incidents.unit*` (sprint 28 change request, global sweep, defect fix).
  const formatDelay = (delay?: number, unit?: string): string => {
    if (!delay) return 'N/A';
    return `${delay} ${(unit && delayUnitTextMap[unit]) || unit?.toLowerCase() || t?.unitHours || 'hours'}`;
  };

  // Helper to get reporter name
  const getReporterName = (incident: Incident): string | undefined => {
    if (incident.user?.firstName || incident.user?.lastName) {
      return `${incident.user.firstName || ''} ${incident.user.lastName || ''}`.trim();
    }
    return undefined;
  };

  if (pendingLoading || acknowledgedLoading) {
    return <Loading size="2xl" center />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-red-500 font-medium">
          <AlertTriangleIcon size={20} />
          <h1 className="text-lg">
            Pending Incidents ({pendingIncidents.length})
          </h1>
        </div>
        {canReportIncident && (
          <Button
            size="md"
            variant="primary"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={() => setIsReportModalOpen(true)}
            data-testid="incidents-page-report-button">
            {t?.reportIncident || 'Report Incident'}
          </Button>
        )}
      </div>

      {pendingIncidents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No pending incidents
        </div>
      ) : (
        <div className="space-y-4">
          {pendingIncidents.map((incident: Incident) => (
            <IncidentCard
              key={incident.incidentId}
              name={incident.name || 'Unnamed Incident'}
              description={incident.description}
              date={formatDate(incident.incidentTime || new Date())}
              statusLabel={incidentStatusTextMap.PENDING || 'PENDING'}
              type={getIncidentTypeDisplay(
                incident.incidentType,
                incident.otherIncidentType,
              )}
              projectName={incident.project?.name}
              delay={formatDelay(incident.delay, incident.delayUnitOfMeasure)}
              documentsCount={incident.documents?.length}
              onClick={() => openIncidentModal(incident)}
              onAcknowledge={() => openIncidentModal(incident)}
            />
          ))}
        </div>
      )}

      {acknowledgedIncidents.length > 0 && (
        <>
          <div className="flex items-center space-x-4 my-6">
            <div className="h-px bg-gray-200 flex-grow"></div>
            <span className="text-gray-500 font-medium text-sm">
              Acknowledged Incidents
            </span>
            <div className="h-px bg-gray-200 flex-grow"></div>
          </div>

          <div className="space-y-4">
            {acknowledgedIncidents.map((incident: Incident) => (
              <DetailedIncidentCard
                key={incident.incidentId}
                name={incident.name || 'Unnamed Incident'}
                status={
                  (incident.status && incidentStatusTextMap[incident.status]) ||
                  incidentStatusTextMap.ACKNOWLEDGED ||
                  'ACKNOWLEDGED'
                }
                additionalInfo={
                  incident.description || 'No additional information'
                }
                date={formatDate(incident.incidentTime || new Date())}
                type={getIncidentTypeDisplay(
                  incident.incidentType,
                  incident.otherIncidentType,
                )}
                delay={formatDelay(incident.delay, incident.delayUnitOfMeasure)}
                projectName={incident.project?.name}
                reportedBy={getReporterName(incident)}
                documentsCount={incident.documents?.length}
                onClick={() => openIncidentModal(incident)}
              />
            ))}
          </div>
        </>
      )}

      <AcknowledgeIncidentModal
        open={isModalOpen}
        incident={selectedIncident}
        isAcknowledging={acknowledgeLoading}
        onConfirm={handleAcknowledgeConfirm}
        onClose={handleModalClose}
      />

      {canReportIncident && (
        <ReportIncidentModal
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
    </div>
  );
}

export default IncidentsPage;
